import { Node, mergeAttributes } from '@tiptap/core';
import { TextSelection } from '@tiptap/pm/state';
import {
  NodeViewContent,
  NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditorState,
} from '@tiptap/react';
import React from 'react';

import { Icon, cn } from '@eluve/components';

export type TemplateGroupAttributes = {
  groupId: string | null;
};

// Private Components

const TemplateGroupView: React.FC<NodeViewProps> = ({
  node,
  editor,
  getPos,
}) => {
  // Highlight the whole group while its title holds the caret (the group
  // bubble menu is open)
  const isGroupActive = useEditorState({
    editor,
    selector: ({ editor: currentEditor }) => {
      if (!currentEditor.isActive('groupTitle')) {
        return false;
      }
      const pos = getPos();
      if (typeof pos !== 'number') {
        return false;
      }
      const { from } = currentEditor.state.selection;
      return from >= pos && from <= pos + node.nodeSize;
    },
  });

  return (
    <NodeViewWrapper
      className={cn(
        // Dashed 2px border: same weight as an active section's ring, the
        // dash signals "this is a group, not a section"
        'group/section-group relative my-2 rounded-lg border-2 border-dashed border-border-secondary p-1',
        {
          'border-bg-indigo-strong-primary bg-bg-indigo-subtle-primary/50':
            isGroupActive,
        },
      )}
    >
      <span
        data-drag-handle
        contentEditable={false}
        // h-6 + top-[3px] centers the handle on the group name's text-base
        // line, optically nudged up a touch
        className={cn(
          // Asymmetric fade: snappy ease-out in on hover, slow ~700ms
          // ease-in-out fade-out (duration + curve are both read from the
          // state being transitioned to — base = out, :hover = in)
          'absolute top-[2px] left-1.5 flex h-6 cursor-grab items-center text-text-tertiary opacity-0 transition-opacity duration-700 ease-in-out group-hover/section-group:opacity-100 group-hover/section-group:duration-150 group-hover/section-group:ease-out',
          { 'text-text-indigo opacity-100': isGroupActive },
        )}
        aria-label="Drag to reorder group"
      >
        <Icon name="GripVertical" size="md" />
      </span>
      <NodeViewContent />
    </NodeViewWrapper>
  );
};

// Public

/**
 * Name line of a template group: plain inline text (no marks) styled
 * as a small uppercase label so groups read differently from sections.
 */
export const GroupTitle = Node.create({
  name: 'groupTitle',
  content: 'inline*',
  marks: '',
  defining: true,

  parseHTML() {
    return [{ tag: 'div[data-group-title]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-group-title': '',
        // pl-6 keeps the name clear of the group drag handle; the wrapper
        // itself only insets 4px so child sections sit near the border
        class:
          'mt-1 mb-2 pl-6 text-base font-semibold tracking-wide text-text-tertiary uppercase',
      }),
      0,
    ];
  },
});

/**
 * A named group of template sections. The group is one draggable unit
 * (all child sections move with it) and maps to a DynamicArtifactTemplate
 * `group` block. Groups cannot nest.
 */
export const TemplateGroup = Node.create({
  name: 'templateGroup',
  // Not in the 'block' group for the same reason as templateSection:
  // it must never nest inside section bodies or other groups.
  content: 'groupTitle templateSection+',
  defining: true,
  isolating: true,
  draggable: true,

  addAttributes() {
    return {
      groupId: { default: null },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-template-group]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-template-group': '' }),
      0,
    ];
  },

  addKeyboardShortcuts() {
    return {
      // Enter on the group name moves into the first section instead of
      // splitting the name
      Enter: () => {
        const { state } = this.editor;
        const { $from } = state.selection;
        if ($from.parent.type.name !== 'groupTitle') {
          return false;
        }
        return this.editor.commands.command(({ tr, dispatch }) => {
          if (dispatch) {
            const afterTitle = $from.after($from.depth);
            tr.setSelection(TextSelection.near(tr.doc.resolve(afterTitle), 1));
            tr.scrollIntoView();
          }
          return true;
        });
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(TemplateGroupView);
  },
});
